import {
	Card,
	Button,
	Tag,
	Space,
	Typography,
	Row,
	Col,
	Descriptions,
	Divider,
	notification,
	Spin,
	Alert,
	Tabs,
	Timeline,
	Statistic,
	Badge,
} from "antd";
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
	PiArrowLeftDuotone,
	PiGearDuotone,
	PiClockDuotone,
	PiPlayCircleDuotone,
	PiCheckCircleDuotone,
	PiXCircleDuotone,
	PiWarningDuotone,
	PiCalendarDuotone,
	PiDatabaseDuotone,
	PiCpuDuotone,
	PiUserDuotone,
	PiListDuotone,
} from "react-icons/pi";
import { requestGetCronLogById } from "../../services/admin.service";
import dayjs from "dayjs";

const { Title, Text } = Typography;

function CronLogViewPage() {
	const navigate = useNavigate();
	const { id } = useParams();
	const [loading, setLoading] = useState(false);
	const [cronLog, setCronLog] = useState(null);

	useEffect(() => {
		document.title = "Cloudiva Console | Cron Log Details";
		fetchCronLogDetails();
	}, [id]);

	const fetchCronLogDetails = () => {
		setLoading(true);
		requestGetCronLogById(id)
			.then((response) => {
				const cronLogData = response.data.data || response.data;
				setCronLog(cronLogData);
			})
			.catch((error) => {
				notification.error({
					message: "Error",
					description: "Failed to fetch cron log details",
				});
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const getStatusColor = (status) => {
		switch (status) {
			case "RUNNING":
				return "processing";
			case "COMPLETED":
				return "success";
			case "FAILED":
				return "error";
			case "CANCELLED":
				return "default";
			default:
				return "default";
		}
	};

	const getStatusIcon = (status) => {
		switch (status) {
			case "RUNNING":
				return <PiPlayCircleDuotone />;
			case "COMPLETED":
				return <PiCheckCircleDuotone />;
			case "FAILED":
				return <PiXCircleDuotone />;
			case "CANCELLED":
				return <PiWarningDuotone />;
			default:
				return <PiClockDuotone />;
		}
	};

	const getJobTypeColor = (jobType) => {
		switch (jobType) {
			case "AUTO_BILLING":
				return "blue";
			case "SYSTEM_CLEANUP":
				return "green";
			case "BACKUP":
				return "orange";
			case "MONITORING":
				return "purple";
			default:
				return "default";
		}
	};

	const getLogLevelColor = (level) => {
		switch (level) {
			case "INFO":
				return "blue";
			case "WARN":
				return "orange";
			case "ERROR":
				return "red";
			case "DEBUG":
				return "default";
			default:
				return "default";
		}
	};

	const formatDuration = (duration) => {
		if (!duration) return "N/A";
		if (duration < 1000) return `${duration}ms`;
		if (duration < 60000) return `${(duration / 1000).toFixed(1)}s`;
		return `${(duration / 60000).toFixed(1)}m`;
	};

	const formatMemoryUsage = (bytes) => {
		if (!bytes) return "N/A";
		const mb = bytes / 1024 / 1024;
		return `${mb.toFixed(1)} MB`;
	};

	if (loading) {
		return (
			<div className="page-content">
				<div
					style={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						height: "400px",
					}}>
					<Spin size="large" />
				</div>
			</div>
		);
	}

	if (!cronLog) {
		return (
			<div className="page-content">
				<Alert
					message="Cron Log Not Found"
					description="The requested cron log could not be found."
					type="error"
					showIcon
				/>
			</div>
		);
	}

	const tabItems = [
		{
			key: "overview",
			label: "Overview",
			children: (
				<Row gutter={[24, 24]}>
					<Col xs={24} lg={12}>
						<Card title="Job Information">
							<Descriptions column={1} size="small" bordered>
								<Descriptions.Item label="Job Name">
									<div style={{ display: "flex", alignItems: "center" }}>
										<PiGearDuotone size={16} style={{ marginRight: 8, color: "#1890ff" }} />
										<Text strong>{cronLog.jobName}</Text>
									</div>
								</Descriptions.Item>
								<Descriptions.Item label="Job Type">
									<Tag color={getJobTypeColor(cronLog.jobType)}>{cronLog.jobType}</Tag>
								</Descriptions.Item>
								<Descriptions.Item label="Status">
									<Tag color={getStatusColor(cronLog.status)} icon={getStatusIcon(cronLog.status)}>
										{cronLog.status}
									</Tag>
								</Descriptions.Item>
								<Descriptions.Item label="Duration">
									<Text strong>{formatDuration(cronLog.duration)}</Text>
								</Descriptions.Item>
								<Descriptions.Item label="Started At">
									<div style={{ display: "flex", alignItems: "center" }}>
										<PiCalendarDuotone size={16} style={{ marginRight: 8, color: "#52c41a" }} />
										<Text>{dayjs(cronLog.startedAt).format("MMMM DD, YYYY HH:mm:ss")}</Text>
									</div>
								</Descriptions.Item>
								{cronLog.completedAt && (
									<Descriptions.Item label="Completed At">
										<div style={{ display: "flex", alignItems: "center" }}>
											<PiCalendarDuotone size={16} style={{ marginRight: 8, color: "#1890ff" }} />
											<Text>{dayjs(cronLog.completedAt).format("MMMM DD, YYYY HH:mm:ss")}</Text>
										</div>
									</Descriptions.Item>
								)}
							</Descriptions>
						</Card>

						{cronLog.errorMessage && (
							<Card title="Error Details" style={{ marginTop: 16 }}>
								<Alert
									message="Job Failed"
									description={cronLog.errorMessage}
									type="error"
									showIcon
									style={{ marginBottom: 16 }}
								/>
								{cronLog.errorStack && (
									<div>
										<Text strong>Stack Trace:</Text>
										<pre
											style={{
												background: "#f5f5f5",
												padding: "12px",
												marginTop: "8px",
												borderRadius: "4px",
												fontSize: "12px",
												overflow: "auto",
											}}>
											{cronLog.errorStack}
										</pre>
									</div>
								)}
							</Card>
						)}
					</Col>

					<Col xs={24} lg={12}>
						<Card title="System Performance">
							<Row gutter={16}>
								<Col span={12}>
									<Statistic
										title="Memory Usage"
										value={formatMemoryUsage(cronLog.metadata?.memoryUsage?.rss)}
										prefix={<PiDatabaseDuotone />}
									/>
								</Col>
								<Col span={12}>
									<Statistic
										title="CPU Usage"
										value={
											cronLog.metadata?.cpuUsage
												? `${(cronLog.metadata.cpuUsage / 1000).toFixed(1)}ms`
												: "N/A"
										}
										prefix={<PiCpuDuotone />}
									/>
								</Col>
							</Row>
							<Divider />
							<Descriptions column={1} size="small">
								<Descriptions.Item label="Process ID">
									<Text code>{cronLog.metadata?.processId || "N/A"}</Text>
								</Descriptions.Item>
								<Descriptions.Item label="Server Instance">
									<div style={{ display: "flex", alignItems: "center" }}>
										<PiDatabaseDuotone size={14} style={{ marginRight: 6, color: "#666" }} />
										<Text>{cronLog.metadata?.serverInstance || "Unknown"}</Text>
									</div>
								</Descriptions.Item>
								<Descriptions.Item label="Triggered By">
									<div style={{ display: "flex", alignItems: "center" }}>
										<PiUserDuotone size={14} style={{ marginRight: 6, color: "#666" }} />
										<Text>{cronLog.metadata?.triggeredBy || "N/A"}</Text>
									</div>
								</Descriptions.Item>
							</Descriptions>
						</Card>

						{cronLog.metadata?.memoryUsage && (
							<Card title="Memory Breakdown" style={{ marginTop: 16 }}>
								<Descriptions column={1} size="small">
									<Descriptions.Item label="RSS">
										<Text>{formatMemoryUsage(cronLog.metadata.memoryUsage.rss)}</Text>
									</Descriptions.Item>
									<Descriptions.Item label="Heap Total">
										<Text>{formatMemoryUsage(cronLog.metadata.memoryUsage.heapTotal)}</Text>
									</Descriptions.Item>
									<Descriptions.Item label="Heap Used">
										<Text>{formatMemoryUsage(cronLog.metadata.memoryUsage.heapUsed)}</Text>
									</Descriptions.Item>
									<Descriptions.Item label="External">
										<Text>{formatMemoryUsage(cronLog.metadata.memoryUsage.external)}</Text>
									</Descriptions.Item>
								</Descriptions>
							</Card>
						)}
					</Col>
				</Row>
			),
		},
		{
			key: "logs",
			label: "Execution Logs",
			children: (
				<Row gutter={[24, 24]}>
					<Col span={24}>
						<Card
							title={
								<Space>
									<PiListDuotone size={20} />
									<span>Log Entries</span>
									<Badge count={cronLog.logs?.length || 0} />
								</Space>
							}>
							{cronLog.logs && cronLog.logs.length > 0 ? (
								<Timeline
									items={cronLog.logs.map((log, index) => ({
										color:
											getLogLevelColor(log.level) === "red"
												? "red"
												: getLogLevelColor(log.level) === "orange"
												? "orange"
												: "blue",
										children: (
											<div>
												<div style={{ display: "flex", alignItems: "center", marginBottom: 4 }}>
													<Tag color={getLogLevelColor(log.level)} size="small">
														{log.level}
													</Tag>
													<Text type="secondary" style={{ fontSize: "12px", marginLeft: 8 }}>
														{dayjs(log.timestamp).format("HH:mm:ss.SSS")}
													</Text>
												</div>
												<div style={{ marginBottom: 8 }}>
													<Text>{log.message}</Text>
												</div>
												{log.data && (
													<div
														style={{
															background: "#f5f5f5",
															padding: "8px",
															borderRadius: "4px",
															fontSize: "12px",
														}}>
														<Text strong>Additional Data:</Text>
														<pre style={{ margin: "4px 0 0 0", overflow: "auto" }}>
															{JSON.stringify(log.data, null, 2)}
														</pre>
													</div>
												)}
											</div>
										),
									}))}
								/>
							) : (
								<Alert
									message="No Logs Available"
									description="This cron job doesn't have any execution logs."
									type="info"
									showIcon
								/>
							)}
						</Card>
					</Col>
				</Row>
			),
		},
		{
			key: "results",
			label: "Job Results",
			children: (
				<Row gutter={[24, 24]}>
					<Col span={24}>
						<Card title="Execution Results">
							{cronLog.results ? (
								<div
									style={{
										background: "#f5f5f5",
										padding: "16px",
										borderRadius: "4px",
									}}>
									<pre style={{ margin: 0, overflow: "auto", fontSize: "13px" }}>
										{JSON.stringify(cronLog.results, null, 2)}
									</pre>
								</div>
							) : (
								<Alert
									message="No Results Available"
									description="This cron job doesn't have any execution results."
									type="info"
									showIcon
								/>
							)}
						</Card>
					</Col>
				</Row>
			),
		},
	];

	return (
		<div className="page-content">
			<div style={{ marginBottom: 24 }}>
				<Space split={<Divider type="vertical" />}>
					<Space>
						<Button icon={<PiArrowLeftDuotone />} onClick={() => navigate("/settings/cron-logs")} />
						<Title level={4} style={{ margin: 0 }}>
							Cron Log Details
						</Title>
						<Tag color={getStatusColor(cronLog.status)} icon={getStatusIcon(cronLog.status)}>
							{cronLog.status}
						</Tag>
					</Space>
				</Space>
			</div>

			<div className="page-content-wrapper">
				<Tabs items={tabItems} defaultActiveKey="overview" size="small" />
			</div>
		</div>
	);
}

export default CronLogViewPage;
